
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import BaseImage from '@/components/common/BaseImage.vue'
import { ProductApiModel } from '@/interfaces/ProductApiModel.js'
import { SummaryItem } from '@/interfaces/SummaryItem'
import { namespace } from 'vuex-class'
import { VBModal } from 'bootstrap-vue'
import EmailModal from '@/components/modals/EmailModal.vue'

import { SummarySection } from '@/interfaces/SummarySection'
import { OptionAdditionalFieldApiModel } from '@/interfaces/OptionAdditionalFieldApiModel'
import { useOptionStore } from '@/store/modules/options'
import { RawLocation, Route } from 'vue-router'
import _ from 'lodash'
import SaveConfigurationModal from '../modals/SaveConfigurationModal.vue'
import axios from 'axios'

const currentModel = namespace('currentModel')

const globalValues = namespace('globalValues')

@Component({
  components: {
    BaseImage,
    EmailModal,
    SaveConfigurationModal
  },
  directives: {
    VBModal
  }
})
export default class ProductSummary extends Vue {
  @Prop({ default: false }) readonly calculatingPrice!: boolean

  public optionStore = useOptionStore();

  public currentDetails: SummaryItem | null = null

  public generatingKey = false

  public generatingPDF = false

  @currentModel.State
  public model!: ProductApiModel

  @currentModel.State
  public configurationKey!: string

  @currentModel.Action
  public saveConfiguration!: () => Promise<boolean>

  @currentModel.Action
  public updateModelProperty!: (payload: { property: string; value: any }) => Promise<void>

  @currentModel.State
  public priceSections!: Array<SummarySection>

  @currentModel.Getter
  public totalPrice!: number

  @globalValues.Action
  public setNotification!: (message?: string) => void

  public mounted() {
    if (window.save) {
      this.$bvModal.show('save-modal')
      delete window.save
    } else if (window.share) {
      this.$bvModal.show('email-modal')
      delete window.share
    }
  }

  public showDetails(priceItem: SummaryItem | null) {
    if (this.currentDetails === priceItem) { this.currentDetails = null } else { this.currentDetails = priceItem }
  }

  public downloadPdf() {
    this.generatingPDF = true

    axios
      .get('/configurator/brochure/' + this.configurationKey, { responseType: 'arraybuffer' })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `Tear Sheet-${this.configurationKey}.pdf`
        link.click()
        this.generatingPDF = false
      })
      .catch(error => {
        this.generatingPDF = false
        this.setNotification('Error generating PDF')
        if (error.response) {
          const response = error.response.data instanceof ArrayBuffer
            ? new TextDecoder().decode(error.response.data)
            : error.response.data
          console.error(response)
        } else {
          console.error(error)
        }
      })
  }

  public copyLinkSuccess() {
    this.setNotification('Copied to clipboard!')
  }

  public copyLinkError() {
    this.setNotification('Press Ctrl+C to copy!')
  }

  get configuratorLink() {
    return location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '') + '/configurator/c/' + this.configurationKey
  }

  public generateKey(callback: () => void) {
    if (this.configurationKey !== undefined) {
      callback()
      return Promise.resolve(true)
    } else {
      this.generatingKey = true
      return this.saveConfiguration()
        .then(() => {
          this.generatingKey = false
          callback()
          return Promise.resolve(true)
        })
    }
  }

  public getRoute(priceItem: SummaryItem) {
    const menuItem = this.optionStore.menuItems.find(mi => mi.categories.some(c => c.code === priceItem.categoryId))

    if (priceItem.changeSection) {
      return { name: priceItem.changeSection }
    } else {
      return { name: 'RoutableOptions', params: { menuItemUrl: menuItem?.code, categoryUrl: priceItem.categoryId } }
    }
  }

  @Watch('$route')
  routeChanged() {
    this.$emit('close')
  }

  public displayPrice(totalPrice: number | undefined, basePrice = false) {
    if (!totalPrice) { return }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })

    return ((basePrice || totalPrice === 0) ? '' : '+') + formatter.format(totalPrice)
  }

  public getPriceItemOption(priceItem: SummaryItem) {
    return priceItem.substitutedFor?.option || priceItem.option
  }

  public getAdditionalFields(priceItem: SummaryItem) {
    const option = this.getPriceItemOption(priceItem)

    if (!option) { return [] }

    return option.additionalFields.filter(field => !field.isReadOnly || this.getAdditionalFieldValue(priceItem, field))
  }

  public getAdditionalFieldType(field: OptionAdditionalFieldApiModel) {
    return field.type === 'Text' ? 'text' : 'number'
  }

  public getAdditionalFieldClass(field: OptionAdditionalFieldApiModel) {
    const classes = field.type === 'Text' ? 'flex-grow-3' : 'flex-grow-1'
    return classes + ' ' + field.cssClasses
  }

  public getAdditionalFieldValue(priceItem: SummaryItem, field: OptionAdditionalFieldApiModel) {
    return this.model.configurator[this.getAdditionalFieldProperty(priceItem, field)]
  }

  public updateAdditionalFieldValue(priceItem: SummaryItem, field: OptionAdditionalFieldApiModel, value: any) {
    const option = this.getPriceItemOption(priceItem)

    if (!option) { return }

    this.updateModelProperty({
      property: this.getAdditionalFieldProperty(priceItem, field),
      value: value
    })
  }

  public getAdditionalFieldProperty(priceItem: SummaryItem, field: OptionAdditionalFieldApiModel) {
    const option = this.getPriceItemOption(priceItem)

    if (!option) { return 'unknown' }

    return option.optionType.modelProperty + '_' + option.code + '_' + field.modelProperty
  }

  public getValidationState({ dirty, validated, valid }: { dirty: boolean; validated: boolean; valid: boolean }) {
    return dirty || validated ? valid : null
  }

  public getAdditionalFieldRules(field: OptionAdditionalFieldApiModel) {
    let rules = 'required'
    if (field.min && field.max) {
      rules += '|between:' + field.min + ',' + field.max
    } else if (field.min) {
      rules += '|min_value:' + field.min
    } else if (field.max) {
      rules += '|max_value:' + field.max
    }

    return rules
  }

  public get notes() {
    return this.model.configurator.notes
  }

  public set notes(value: string) {
    this.updateModelProperty({
      property: 'notes',
      value: value
    })
  }

  public editable(priceItem: SummaryItem, field: OptionAdditionalFieldApiModel) {
    if (field.isReadOnly) {
      return false
    }

    const allItems = this.priceSections.flatMap(s => s.items)

    const firstIndex = allItems.findIndex(i => i.optionId === priceItem.optionId)
    const index = allItems.findIndex(i => i === priceItem)
    return firstIndex === index
  }

  canChange(priceItem: SummaryItem) {
    return (priceItem.changeSection || priceItem.categoryId) && !this.getPriceItemOption(priceItem)?.isHidden
  }

  canRemove(priceItem: SummaryItem) {
    return this.getPriceItemOption(priceItem) && this.getPriceItemOption(priceItem)?.removable && !this.getPriceItemOption(priceItem)?.isHidden
  }

  removeOption(priceItem: SummaryItem) {
    // Need to navigate first because OptionSelector component handles removeOption event
    this.changeOption(priceItem, () => this.$root.$emit('removeOption', this.getPriceItemOption(priceItem)))
  }

  changeOption(priceItem: SummaryItem, onComplete?: (route: Route) => void) {
    const routeData = this.getRoute(priceItem)
    const route = this.$router.resolve(routeData as RawLocation)
    if (route.resolved.fullPath === this.$route.fullPath) {
      this.$emit('close')
      if (onComplete) { onComplete(route.resolved) }
    } else {
      this.$router.push(routeData as RawLocation, onComplete)
    }
  }

  groupedItems(items: SummaryItem[]) {
    const a = _.groupBy(items.filter(i => !i.onlyInBrochure), elem => elem.name)
    return a
  }

  async save() {
    // await api.post('/save/' + this.configurationKey)
  }

  public get isAuthenticated() {
    return window.isAuthenticated
  }

  public openSaveModal() {
    if (this.isAuthenticated) {
      this.$bvModal.show('save-modal')
    } else {
      window.location.href = '/login?configurationKey=' + this.configurationKey
    }
  }

  public openEmailModal() {
    this.$bvModal.show('email-modal')
  }
}
